import type {CreateExtArgs} from '@wix/document-manager-core'
import type {ComponentAfterAddDataEvent, ComponentBeforeAddDataEvent} from '../hooks'
import {addPlatformAppToPageIfFirstController, setControllerStateFromSerializedStructure} from './utils/platformAppComp'
import {getControllerAppDefinitionId} from '../../appControllerData/appControllerUtils'
import type {MetadataHookEventData} from '../../componentsMetadata/metadataHooks'
import type {StructureExtensionAPI} from '../../structure'
import type {ComponentsMetadataAPI} from '../../componentsMetadata/componentsMetadata'
import {COMP_TYPES} from '../../../constants/constants'
import _ from 'lodash'
import type {CompStructure, CompLayout} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'

export const handleAppWidgetAddition = (createExtArgs: CreateExtArgs, data: ComponentAfterAddDataEvent) => {
    const {compToAddPointer, componentDefinition} = data
    setControllerStateFromSerializedStructure(createExtArgs, compToAddPointer, componentDefinition)
    addPlatformAppToPageIfFirstController(
        createExtArgs,
        compToAddPointer,
        getControllerAppDefinitionId(componentDefinition.data)!
    )
}

export const isContainableByStructureInAppWidget = (
    {extensionAPI}: CreateExtArgs,
    {additionalArguments}: MetadataHookEventData,
    originalMetadataValue?: boolean
): boolean | undefined => {
    if (originalMetadataValue === false) {
        return false
    }

    const {components, componentsMetadata} = extensionAPI as StructureExtensionAPI & ComponentsMetadataAPI
    const [compStructure, potentialContainerPointer] = additionalArguments

    if (components.getComponentType(potentialContainerPointer) === COMP_TYPES.APP_WIDGET_TYPE) {
        const rootChild = _.head(components.getChildren(potentialContainerPointer))
        if (!rootChild) {
            return originalMetadataValue
        }
        return componentsMetadata.isContainableByStructurePublic(compStructure, rootChild)
    }

    return true
}

const verifyAppWidgetStructure = (serializedComp: CompStructure) => {
    if (_.size(serializedComp.components) !== 1) {
        throw new ReportableError({
            errorType: 'APP_WIDGET_ADDED_WITH_INVALID_CHILDREN_AMOUNT',
            message: 'Invalid app widget structure - must have single child'
        })
    }
}

const DIMENSIONS: (keyof CompLayout)[] = ['width', 'height']

const adjustLayout = (serializedComp: CompStructure) => {
    const isResponsiveLayout = serializedComp.layouts
    if (isResponsiveLayout) {
        return
    }

    const widgetRootStructure = serializedComp.components?.[0] as CompStructure
    _.merge(widgetRootStructure, {layout: {x: 0, y: 0}})
    _.forEach(DIMENSIONS, dim => {
        if (_.has(serializedComp, ['layout', dim])) {
            widgetRootStructure.layout![dim] = serializedComp?.layout?.[dim]
        } else if (_.has(widgetRootStructure, ['layout', dim])) {
            _.set(serializedComp, ['layout', dim], widgetRootStructure.layout![dim])
        }
    })
}

export const appWidgetBeforeAdd = (createExtArgs: CreateExtArgs, data: ComponentBeforeAddDataEvent) => {
    verifyAppWidgetStructure(data.componentDefinition)
    adjustLayout(data.componentDefinition)
}
